/**
 * HarvestForemanTable Component
 * Table for the per-foreman-group (kemandoran) monitoring report. Mirrors the
 * per-location table but the second level is the foreman group, and only actual
 * (BCC/SPB) metrics are shown — planning/target columns are omitted.
 */

import React from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Spinner,
    Center,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    HStack,
    IconButton,
    VStack,
    Image,
    Flex,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    Divider,
} from '@chakra-ui/react';
import angkongIcon from '../../../assets/icons/angkong_icon.svg';
import cautionIcon from '../../../assets/icons/warning.png';
import dtIcon from '../../../assets/icons/dump-truck_icon.svg';
import { ChevronDownIcon, ChevronRightIcon, DownloadIcon } from '@chakra-ui/icons';
import { useState } from 'react';
import type { HarvestForemanRecord } from '../types';
import { formatDate, formatNumber, formatInteger } from '../../harvest/utils';
import { TphDetailRows } from '../../harvest/components/TphDetailRows';
import { useImagePreview, ImagePreviewModal } from '../../harvest/components/ImagePreview';
import { downloadHarvestForemanPDF } from '../utils/pdfGenerator';

interface HarvestForemanTableProps {
    data: HarvestForemanRecord[];
    loading: boolean;
    error: string | null;
}

// Number of columns in the detail table (used for empty-state colSpan).
const COLUMN_COUNT = 12;

export const HarvestForemanTable: React.FC<HarvestForemanTableProps> = ({
    data,
    loading,
    error,
}) => {
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const { isOpen, onClose, previewImageUrl, previewImageTitle, handlePreviewImage } = useImagePreview();
    const [warningModalData, setWarningModalData] = useState<{
        isOpen: boolean;
        date: string;
        foremanGroup: string;
        foremanName: string;
    }>({
        isOpen: false,
        date: '',
        foremanGroup: '',
        foremanName: '',
    });

    const toggleRow = (rowKey: string) => {
        setExpandedRows(prev => ({
            ...prev,
            [rowKey]: !prev[rowKey],
        }));
    };

    if (loading) {
        return (
            <Center py={10}>
                <Spinner size="xl" color="green.500" thickness="4px" />
            </Center>
        );
    }

    if (error) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Box>
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Box>
            </Alert>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription>Tidak ada data untuk ditampilkan</AlertDescription>
            </Alert>
        );
    }

    return (
        <VStack spacing={6} align="stretch">
            {data.map((rkhRecord) => {
                return (
                    <Box key={rkhRecord.rkh_nbr}>
                        {/* lvl 1 header */}
                        <Box borderRadius="lg" boxShadow="sm" mb={0} overflow="hidden" bg="white">
                            <Flex direction={{ base: 'column', lg: 'row' }} align="stretch">
                                <Box bg="#e7efdf" p={4} flex="2.5">
                                    <Text color="black"><b>DIVISI | - </b>{rkhRecord.division}</Text>
                                    <Text color="black" whiteSpace="nowrap">{rkhRecord.rkh_nbr}</Text>
                                </Box>
                                <Box bg="white" p={4} flex="1">
                                    <HStack spacing={4}>
                                        <Box
                                            bg="#ededed"
                                            p={2}
                                            borderRadius="lg"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            flexShrink={0}
                                        >
                                            <Image src={angkongIcon} alt="Angkong Icon" boxSize="25px" objectFit="contain" />
                                        </Box>
                                        <Box>
                                            <Text color="black"><b>Hasil Panen </b>BCC</Text>
                                            <Text color="black"><b>{formatInteger(rkhRecord.subtotal.total_bcc_weight)} </b>Kg</Text>
                                        </Box>
                                    </HStack>
                                </Box>

                                <Box bg="white" p={4} flex="1">
                                    <HStack spacing={4}>
                                        <Box
                                            bg="red.100"
                                            p={2}
                                            borderRadius="lg"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            flexShrink={0}
                                        >
                                            <Image src={cautionIcon} alt="Caution Icon" boxSize="25px" objectFit="contain" />
                                        </Box>
                                        <Box>
                                            <Text color="black"><b>Total Restan </b></Text>
                                            <Text color="black"><b>{formatInteger(rkhRecord.subtotal.total_restan_weight)} </b>Kg</Text>
                                        </Box>
                                    </HStack>
                                </Box>

                                <Box bg="white" p={4} flex="1">
                                    <HStack spacing={4}>
                                        <Box
                                            bg="#ededed"
                                            p={2}
                                            borderRadius="lg"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            flexShrink={0}
                                        >
                                            <Image src={dtIcon} alt="dt Icon" boxSize="25px" objectFit="contain" />
                                        </Box>
                                        <Box>
                                            <Text color="black"><b>Ritase </b></Text>
                                            <Text color="black"><b>{rkhRecord.subtotal.ritase} </b></Text>
                                        </Box>
                                    </HStack>
                                </Box>
                            </Flex>
                        </Box>

                        <Box overflowX="auto" bg="white" borderRadius="lg" boxShadow="sm" mt={3}>
                            <Table variant="unstyled" size="sm">
                                <Thead bg="white">
                                    {/* lvl 2 group header */}
                                    <Tr bg="#4A5A33" borderTopLeftRadius="lg" borderTopRightRadius="lg">
                                        <Th colSpan={5} py={2} px={3}>
                                            <Text whiteSpace="nowrap" color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                Informasi Kemandoran
                                            </Text>
                                        </Th>
                                        <Th colSpan={3} borderLeft="1px solid" borderColor="whiteAlpha.300" borderBottom="none">
                                            <Text whiteSpace="nowrap" color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                Janjang
                                            </Text>
                                        </Th>
                                        <Th colSpan={3} borderLeft="1px solid" borderColor="whiteAlpha.300" borderBottom="none">
                                            <Text whiteSpace="nowrap" color="white" fontSize="2xs" fontWeight="normal" textAlign="center">
                                                Berat (kg)
                                            </Text>
                                        </Th>
                                    </Tr>
                                    <Tr bg="#e7efdf">
                                        <Th><Text whiteSpace="nowrap">Kemandoran</Text></Th>
                                        <Th isNumeric textAlign="right">HK BCC</Th>
                                        <Th isNumeric textAlign="right">Jml BCC</Th>
                                        <Th isNumeric textAlign="right">Jml SPB</Th>
                                        <Th isNumeric textAlign="right">BCC Restan</Th>
                                        <Th isNumeric
                                            borderLeft="1px solid"
                                            borderColor="blackAlpha.300"
                                            borderBottom="none"
                                            textAlign="right">Janjang BCC</Th>
                                        <Th isNumeric textAlign="right">Janjang SPB</Th>
                                        <Th isNumeric textAlign="right">Janjang Restan</Th>
                                        <Th isNumeric
                                            borderLeft="1px solid"
                                            borderColor="blackAlpha.300"
                                            borderBottom="none"
                                            textAlign="right">Berat BCC (kg)</Th>
                                        <Th isNumeric textAlign="right">Berat SPB (kg)</Th>
                                        <Th isNumeric textAlign="right">Berat Restan (kg)</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {rkhRecord.foremen && rkhRecord.foremen.length > 0 ? (
                                        rkhRecord.foremen.map((foreman, index) => {
                                            const rowKey = `${rkhRecord.rkh_nbr}-${foreman.foreman_group_code}-${index}`;
                                            const isExpanded = expandedRows[rowKey] || false;
                                            const hasInvalidHarvestArea = foreman.tph_details?.some(
                                                (tph) => (!tph.ha_per_tph || tph.ha_per_tph <= 0) && tph.bunch_qty > 0
                                            );
                                            return (
                                                <React.Fragment key={rowKey}>
                                                    <Tr _hover={{ bg: 'gray.50' }}>
                                                        <Td>
                                                            <HStack spacing={2} align="center">
                                                                <Box>
                                                                    <Text fontWeight="bold" fontSize="xs" color="gray.700">
                                                                        {foreman.foreman_group_name || '-'}
                                                                    </Text>
                                                                    {foreman.foreman_group_code && (
                                                                        <Text fontSize="2xs" color="gray.500">
                                                                            {foreman.foreman_group_code}
                                                                        </Text>
                                                                    )}
                                                                </Box>
                                                                <IconButton 
                                                                    aria-label='Download data'
                                                                    icon={<DownloadIcon />}
                                                                    size="xs"
                                                                    variant="ghost"
                                                                    color={hasInvalidHarvestArea ? "gray.400" : "green.600"}
                                                                    cursor="pointer"
                                                                    _hover={{ bg: hasInvalidHarvestArea ? 'gray.50' : 'gray.100' }}
                                                                    onClick={() => {
                                                                        if (hasInvalidHarvestArea) {
                                                                            const mandorNames = Array.from(
                                                                                new Set(
                                                                                    foreman.tph_details
                                                                                        ?.map((tph) => tph.foreman_name)
                                                                                        .filter(Boolean)
                                                                                )
                                                                            ).join(', ') || '-';

                                                                            setWarningModalData({
                                                                                isOpen: true,
                                                                                date: rkhRecord.rkh_date,
                                                                                foremanGroup: foreman.foreman_group_name || foreman.foreman_group_code || '-',
                                                                                foremanName: mandorNames,
                                                                            });
                                                                            return;
                                                                        }
                                                                        downloadHarvestForemanPDF(foreman);
                                                                    }}
                                                                />
                                                                {foreman.tph_details && foreman.tph_details.length > 0 && (
                                                                    <IconButton aria-label='Expand row'
                                                                        icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                                                        size="xs"
                                                                        variant="ghost"
                                                                        onClick={() => toggleRow(rowKey)}
                                                                    />
                                                                )}
                                                            </HStack>
                                                        </Td>
                                                        <Td isNumeric textAlign="right">{formatNumber(foreman.bcc_hk)}</Td>
                                                        <Td isNumeric textAlign="right">{formatNumber(foreman.bcc_cnt)}</Td>
                                                        <Td isNumeric textAlign="right">{formatNumber(foreman.spb_cnt)}</Td>
                                                        <Td isNumeric textAlign="right">{formatNumber(foreman.bcc_restan)}</Td>
                                                        <Td isNumeric
                                                            textAlign="right"
                                                            borderLeft="1px solid"
                                                            borderColor="blackAlpha.300">{formatNumber(foreman.bcc_bunch)}</Td>
                                                        <Td isNumeric textAlign="right">{formatNumber(foreman.spb_bunch)}</Td>
                                                        <Td isNumeric textAlign="right">{formatNumber(foreman.jjg_restan)}</Td>
                                                        <Td isNumeric
                                                            textAlign="right"
                                                            borderLeft="1px solid"
                                                            borderColor="blackAlpha.300">{formatInteger(foreman.bcc_weight)}</Td>
                                                        <Td isNumeric textAlign="right">{formatInteger(foreman.spb_weight)}</Td>
                                                        <Td isNumeric textAlign="right">{formatInteger(foreman.restan_weight)}</Td>
                                                    </Tr>

                                                    {isExpanded && foreman.tph_details && foreman.tph_details.length > 0 ? (
                                                        <TphDetailRows
                                                            details={foreman.tph_details}
                                                            onPreviewImage={handlePreviewImage}
                                                            wrapColSpan={COLUMN_COUNT}
                                                        />
                                                    ) : null}
                                                </React.Fragment>
                                            );
                                        })
                                    ) : (
                                        <Tr>
                                            <Td colSpan={COLUMN_COUNT} py={6}>
                                                <VStack spacing={1} justify="center" opacity={0.6}>
                                                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                                                        Tidak ada kemandoran untuk RKH ini
                                                    </Text>
                                                </VStack>
                                            </Td>
                                        </Tr>
                                    )}

                                    {/* Subtotal Row */}
                                    {rkhRecord.foremen && rkhRecord.foremen.length > 0 && (
                                        <Tr bg="#e7efdf" borderTop="1px solid" borderColor="blackAlpha.100" borderBottomLeftRadius="lg" borderBottomRightRadius="lg">
                                            <Td colSpan={2} fontWeight="bold" borderBottomLeftRadius="lg">
                                                TOTAL
                                            </Td>
                                            <Td isNumeric fontWeight="bold" textAlign="right">{formatNumber(rkhRecord.subtotal.total_bcc_cnt)}</Td>
                                            <Td isNumeric fontWeight="bold" textAlign="right">{formatNumber(rkhRecord.subtotal.total_spb_cnt)}</Td>
                                            <Td isNumeric fontWeight="bold" textAlign="right">{formatNumber(rkhRecord.subtotal.total_bcc_restan)}</Td>
                                            <Td isNumeric fontWeight="bold"
                                                textAlign="right"
                                                borderLeft="1px solid"
                                                borderColor="blackAlpha.300">{formatNumber(rkhRecord.subtotal.total_bcc_bunch)}</Td>
                                            <Td isNumeric fontWeight="bold" textAlign="right">{formatNumber(rkhRecord.subtotal.total_spb_bunch)}</Td>
                                            <Td isNumeric fontWeight="bold" textAlign="right">{formatNumber(rkhRecord.subtotal.total_jjg_restan)}</Td>
                                            <Td isNumeric fontWeight="bold"
                                                textAlign="right"
                                                borderLeft="1px solid"
                                                borderColor="blackAlpha.300">{formatNumber(rkhRecord.subtotal.total_bcc_weight)}</Td>
                                            <Td isNumeric fontWeight="bold" textAlign="right">{formatNumber(rkhRecord.subtotal.total_spb_weight)}</Td>
                                            <Td isNumeric fontWeight="bold"
                                                textAlign="right"
                                                borderBottomRightRadius="lg">{formatNumber(rkhRecord.subtotal.total_restan_weight)}</Td>
                                        </Tr>
                                    )}
                                </Tbody>
                            </Table>
                        </Box>

                        {/* Divisi Footer */}
                        {rkhRecord.foremen && rkhRecord.foremen.length > 0 && (
                            <Box mt={2} px={1}>
                                <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                                    DIVISI <Text as="span" fontWeight="bold" color="gray.700">{rkhRecord.division}</Text>{' '}
                                    /{' '}
                                    <Text as="span" fontWeight="bold" color="gray.700">{rkhRecord.rkh_nbr}</Text>{' '}
                                    / TANGGAL:{' '}
                                    <Text as="span" fontWeight="bold" color="gray.700">{formatDate(rkhRecord.rkh_date)}</Text>{' '}
                                    / USER{' '}
                                    <Text as="span" fontWeight="bold" color="gray.700">{rkhRecord.rkh_user}</Text>{' '}
                                    / STAGE{' '}
                                    <Text as="span" fontWeight="bold" color={rkhRecord.rkh_stage === 'C' ? 'green.500' : rkhRecord.rkh_stage === 'P' ? 'yellow.500' : 'gray.700'}>
                                        {rkhRecord.rkh_stage}
                                    </Text>
                                </Text>
                            </Box>
                        )}
                    </Box>
                );
            })}

            {/* Image Preview Modal */}
            <ImagePreviewModal
                isOpen={isOpen}
                onClose={onClose}
                previewImageUrl={previewImageUrl}
                previewImageTitle={previewImageTitle}
            />

            {/* Invalid Harvest Area Warning Modal */}
            <Modal
                isOpen={warningModalData.isOpen}
                onClose={() => setWarningModalData((prev) => ({ ...prev, isOpen: false }))}
                isCentered
            >
                <ModalOverlay />
                <ModalContent borderRadius="xl" mx={4} overflow="hidden" boxShadow="2xl">
                    <ModalHeader bg="customGold.500" color="white" py={3} px={4} fontSize="md" fontWeight="bold">
                        Download Gagal
                    </ModalHeader>
                    <ModalCloseButton color="white" />
                    <ModalBody py={5} px={5}>
                        <VStack align="stretch" spacing={3}>
                            <Text fontSize="sm" fontWeight="bold" color="red.500" textAlign="center">
                                Terdapat pemanen dengan HA kosong!
                            </Text>
                            <Box bg="gray.50" p={3} borderRadius="md" border="1px solid" borderColor="gray.200">
                                <VStack align="stretch" spacing={2} fontSize="xs">
                                    <Flex justify="space-between">
                                        <Text color="gray.500" fontWeight="medium">Tanggal Panen:</Text>
                                        <Text fontWeight="bold" color="gray.800">{formatDate(warningModalData.date)}</Text>
                                    </Flex>
                                    <Flex justify="space-between">
                                        <Text color="gray.500" fontWeight="medium">Kemandoran:</Text>
                                        <Text fontWeight="bold" color="gray.800">{warningModalData.foremanGroup}</Text>
                                    </Flex>
                                    <Flex justify="space-between">
                                        <Text color="gray.500" fontWeight="medium">Nama Mandor:</Text>
                                        <Text fontWeight="bold" color="gray.800">{warningModalData.foremanName}</Text>
                                    </Flex>
                                </VStack>
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter bg="gray.50" py={3} px={4}>
                        <Button
                            size="sm"
                            bg="customGold.500"
                            color="white"
                            _hover={{ bg: 'customGold.600' }}
                            onClick={() => setWarningModalData((prev) => ({ ...prev, isOpen: false }))}
                        >
                            Tutup
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </VStack>
    );
};
